/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';

/**
 * Service for managing localStorage operations with type safety and error handling
 *
 * Features:
 * - Type-safe get/set operations with generics
 * - Optional key namespacing to avoid collisions
 * - SSR-safe with localStorage availability check
 * - Comprehensive error logging
 */
@Injectable({
  providedIn: 'root'
})
export class DataStoreService {
  private readonly keyPrefix = '';  // Empty prefix for backward compatibility
  private isLocalStorageAvailable: boolean;

  constructor() {
    this.isLocalStorageAvailable = this.checkLocalStorageAvailability();
  }

  /**
   * Checks if localStorage is available (SSR-safe)
   * @returns true if localStorage is available, false otherwise
   */
  private checkLocalStorageAvailability(): boolean {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn('localStorage is not available:', e);
      return false;
    }
  }

  /**
   * Generates the full key with prefix
   * @param key - The base key
   * @returns The prefixed key
   */
  private getFullKey(key: string): string {
    return `${this.keyPrefix}${key}`;
  }

  /**
   * Sets a string value in localStorage
   * @param key - The storage key
   * @param value - The string value to store
   * @returns true if successful, false otherwise
   */
  set(key: string, value: string): boolean {
    if (!this.isLocalStorageAvailable) {
      console.error('DataStoreService: localStorage is not available');
      return false;
    }

    try {
      localStorage.setItem(this.getFullKey(key), value);
      return true;
    } catch (error) {
      console.error(`DataStoreService: Failed to set key "${key}"`, error);
      return false;
    }
  }

  /**
   * Gets a string value from localStorage
   * @param key - The storage key
   * @returns The stored string value or null if not found
   */
  get(key: string): string | null {
    if (!this.isLocalStorageAvailable) {
      return null;
    }

    try {
      return localStorage.getItem(this.getFullKey(key));
    } catch (error) {
      console.error(`DataStoreService: Failed to get key "${key}"`, error);
      return null;
    }
  }

  /**
   * Sets an object in localStorage with JSON serialization
   * @param key - The storage key
   * @param obj - The object to store
   * @returns true if successful, false otherwise
   */
  setObject(key: string, obj: object): boolean {
    if (!this.isLocalStorageAvailable) {
      console.error('DataStoreService: localStorage is not available');
      return false;
    }

    try {
      const serialized = JSON.stringify(obj);
      localStorage.setItem(this.getFullKey(key), serialized);
      return true;
    } catch (error) {
      console.error(`DataStoreService: Failed to set object for key "${key}"`, error);
      return false;
    }
  }

  /**
   * Gets an object from localStorage with JSON deserialization
   * @param key - The storage key
   * @returns The stored object or null if not found/invalid
   */
  getObject(key: string): any {
    if (!this.isLocalStorageAvailable) {
      return null;
    }

    try {
      const raw = localStorage.getItem(this.getFullKey(key));
      if (!raw) {
        return null;
      }

      return JSON.parse(raw);
    } catch (error) {
      console.error(`DataStoreService: Failed to get object for key "${key}"`, error);
      return null;
    }
  }

  /**
   * Removes an item from localStorage
   * @param key - The storage key
   * @returns void
   */
  remove(key: string): void {
    if (!this.isLocalStorageAvailable) {
      return;
    }

    try {
      localStorage.removeItem(this.getFullKey(key));
    } catch (error) {
      console.error(`DataStoreService: Failed to remove key "${key}"`, error);
    }
  }

  /**
   * Removes every item from localStorage except the given keys.
   * Used on sign-out so no account data is left behind, while UI preferences (e.g. language) survive.
   * @param keepKeys - Full storage keys to keep
   */
  clearExcept(keepKeys: string[]): void {
    if (!this.isLocalStorageAvailable) {
      return;
    }

    try {
      const keep = new Set(keepKeys);
      Object.keys(localStorage)
        .filter(key => !keep.has(key))
        .forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('DataStoreService: Failed to clear storage', error);
    }
  }

  /**
   * Clears all items from localStorage
   * @returns void
   */
  clear(): void {
    if (!this.isLocalStorageAvailable) {
      return;
    }

    try {
      localStorage.clear();
    } catch (error) {
      console.error('DataStoreService: Failed to clear storage', error);
    }
  }
}
